import { useEffect, useState } from 'react';
import { Search, ShieldAlert, X } from 'lucide-react';
import { searchUsers } from '../../services/api.js';
import styles from './MasqueradeModal.module.css';

export default function MasqueradeModal({ onClose, onStart }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (query.trim().length < 2 || selected) { setResults([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const users = await searchUsers(query.trim());
        if (!cancelled) setResults(users);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not search users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, selected]);

  useEffect(() => {
    const onKeyDown = event => { if (event.key === 'Escape' && !starting) onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, starting]);

  const confirm = async () => {
    setStarting(true);
    setError('');
    try {
      await onStart(selected.id);
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not start masquerading');
      setStarting(false);
    }
  };

  return (
    <div className={styles.backdrop} role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !starting) onClose();
    }}>
      <section className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="masquerade-title">
        <div className={styles.heading}>
          <div>
            <span className={styles.eyebrow}>Admin tool</span>
            <h2 id="masquerade-title">Masquerade as a user</h2>
          </div>
          <button className={styles.close} onClick={onClose} disabled={starting} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {selected ? (
          <>
            <div className={styles.warning}>
              <ShieldAlert size={22} aria-hidden="true" />
              <div>
                <strong>You will have full access as this user.</strong>
                <span>Uploads, settings, shares, API keys, and destructive actions affect the real account.</span>
              </div>
            </div>
            <div className={styles.selectedUser}>
              <strong>{selected.display_name || selected.email}</strong>
              {selected.display_name && <span>{selected.email}</span>}
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.actions}>
              <button className={styles.secondary} onClick={() => { setSelected(null); setError(''); }} disabled={starting}>
                Back
              </button>
              <button className={styles.danger} onClick={confirm} disabled={starting}>
                {starting ? 'Switching…' : 'Start masquerading'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className={styles.description}>Search by display name or email address.</p>
            <label className={styles.searchBox}>
              <Search size={17} aria-hidden="true" />
              <input autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Name or email" />
            </label>
            <div className={styles.results} aria-live="polite">
              {loading && <div className={styles.status}>Searching…</div>}
              {!loading && query.trim().length >= 2 && results.length === 0 && !error && (
                <div className={styles.status}>No users found.</div>
              )}
              {results.map(user => (
                <button key={user.id} className={styles.userRow} onClick={() => setSelected(user)}>
                  <strong>{user.display_name || user.email}</strong>
                  {user.display_name && <span>{user.email}</span>}
                </button>
              ))}
            </div>
            {error && <div className={styles.error}>{error}</div>}
          </>
        )}
      </section>
    </div>
  );
}
